import { useContext, useCallback, useState } from 'react';
import _map from 'lodash/map';

import { formatListData, AjaxContext, UserContext, MerchantContext } from 'commons-ui';
import { IApp, IPageDetails, IPermission, IPermissionSet, UserV2 } from 'models/settingsV2.model';
import { SERVICE_NAMES } from '../../../../settingsV2Enum';
import { PERMISSION_TYPE, userUrlPrefix, unauthorizedUrlPrefix, RESOURCES, SETTINGS_V2_ACTION } from '../../../../settingsV2Constant';

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const { user } = useContext(UserContext);
	const { merchantName } = useContext(MerchantContext);
	const [forceUpdate, setForceUpdate] = useState(false);
	const turnForceUpdateOff = () => {
		setForceUpdate(false);
	};

	const getUsersData = useCallback(async ({ app, pageDetails }: { app: IApp; pageDetails: IPageDetails }) => {
		const res = await postData({
			endpoint: `${userUrlPrefix}-listUsers`,
			body: {
				pageNo: pageDetails.pageNo,
				pageSize: pageDetails.pageSize,
				userMerchant: merchantName,
				forwardReqRoute: `/dashboard/${RESOURCES.user}/${SETTINGS_V2_ACTION.listUsers}`,
				forwardReqToService: SERVICE_NAMES.AUTH
			},
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<UserV2> = formatListData(res?.data?.data?.users);
		return { rowCount: res?.data?.data?.userCount, data: listData };
	}, []);

	const requestUpdateUser = useCallback((appName: string, data: UserV2) => {
		return postData({
			endpoint: `${userUrlPrefix}-editUser`,
			body: {
				...data,
				userMerchant: merchantName,
				forwardReqRoute: `/dashboard/${RESOURCES.user}/${SETTINGS_V2_ACTION.editUser}`,
				forwardReqToService: SERVICE_NAMES.AUTH
			},
			queryParams: {
				appName
			}
		});
	}, []);

	const requestCreateUser = useCallback((appName: string, data: UserV2) => {
		return postData({
			endpoint: `${userUrlPrefix}-createUser`,
			body: {
				...data,
				userMerchant: merchantName,
				updatedBy: user?.email,
				forwardReqRoute: `/dashboard/${RESOURCES.user}/${SETTINGS_V2_ACTION.createUser}`,
				forwardReqToService: SERVICE_NAMES.AUTH
			},
			queryParams: {
				appName
			}
		});
	}, []);

	const requestDeleteUser = useCallback((appName: string, data: UserV2) => {
		return postData({
			endpoint: `${userUrlPrefix}-deleteUsers`,
			body: {
				userId: data._id,
				email: data.email,
				userMerchant: merchantName,
				forwardReqRoute: `/dashboard/${RESOURCES.user}/${SETTINGS_V2_ACTION.deleteUsers}`,
				forwardReqToService: SERVICE_NAMES.AUTH
			},
			queryParams: {
				appName
			}
		});
	}, []);

	const getPermissionsOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSIONS], userMerchant: merchantName },
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<IPermission> = res?.data?.dropdownOptions?.permissions;
		const formattedListData = _map(listData, (permission: IPermission) => ({
			id: permission._id,
			label: permission.actionName
		}));
		return formattedListData;
	}, []);
	const getPermissionSetOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSION_SETS], userMerchant: merchantName },
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<IPermissionSet> = res?.data?.dropdownOptions?.permissionSets;
		const formattedListData = _map(listData, (permissionSets: IPermissionSet) => ({
			id: permissionSets._id,
			label: permissionSets.name
		}));
		return formattedListData;
	}, []);
	const getManagerOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSIONS], userMerchant: merchantName },
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<string> = res?.data?.dropdownOptions?.managers;
		const formattedListData = _map(listData, (managers: string) => ({
			id: managers,
			label: managers
		}));
		return formattedListData;
	}, []);
	const getDepartmentOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSIONS], userMerchant: merchantName },
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<string> = res?.data?.dropdownOptions?.department;
		const formattedListData = _map(listData, (department: string) => ({
			id: department,
			label: department
		}));
		return formattedListData;
	}, []);
	const getDesignationOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSIONS], userMerchant: merchantName },
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<string> = res?.data?.dropdownOptions?.designation;
		const formattedListData = _map(listData, (designation: string) => ({
			id: designation,
			label: designation
		}));
		return formattedListData;
	}, []);
	const getTeamOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSIONS], userMerchant: merchantName },
			queryParams: {
				appName: app.appName
			}
		});
		const listData: Array<string> = res?.data?.dropdownOptions?.teams;
		const formattedListData = _map(listData, (teams: string) => ({
			id: teams,
			label: teams
		}));
		return formattedListData;
	}, []);
	const getRoleNameTempOptions = useCallback(async ({ app }: { app: IApp }) => {
		const res = await postData({
			endpoint: `${unauthorizedUrlPrefix}/fetchACLDropdownOptions`,
			body: { fields: [PERMISSION_TYPE.PERMISSIONS], userMerchant: merchantName },
			queryParams: { appName: app.appName }
		});
		const listData: Array<string> = res?.data?.dropdownOptions?.roleNameTemp;
		const formattedListData = _map(listData, (roleNameTemp: string) => ({
			id: roleNameTemp,
			label: roleNameTemp
		}));
		return formattedListData;
	}, []);

	return {
		getUsersData,
		requestUpdateUser,
		requestCreateUser,
		requestDeleteUser,
		getPermissionsOptions,
		getPermissionSetOptions,
		getManagerOptions,
		getDepartmentOptions,
		getDesignationOptions,
		getTeamOptions,
		getRoleNameTempOptions,
		forceUpdate,
		setForceUpdate,
		turnForceUpdateOff
	};
};

export default useService;
